import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { createService, errorMessage, listTargetServices, reuseSetlist } from './libraryApi';
import { formatServiceDate, pluralize } from './libraryFormat';

const emptyNewService = { title: '', service_date: '', start_time: '10:00', end_time: '11:30' };

/**
 * "Gebruik als nieuwe setlist": copies the song order into a planned service through the existing setlist logic.
 * Never replaces an existing setlist: a service that already has one is only extended, and only after an explicit choice.
 */
const ReuseDialog = ({ setlist, open, onOpenChange, onDone }) => {
  const [targets, setTargets] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [target, setTarget] = useState('');
  const [newService, setNewService] = useState(emptyNewService);
  const [mode, setMode] = useState('');
  const [unlinked, setUnlinked] = useState('as_title');
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // A new service created before a failed copy is reused on retry instead of creating a second empty one.
  const createdServiceId = useRef(null);

  useEffect(() => {
    if (!open) return;
    createdServiceId.current = null;
    setTarget(''); setMode(''); setConfirmed(false); setError(''); setNewService(emptyNewService);
    listTargetServices().then(setTargets).catch((err) => setLoadError(errorMessage(err, 'Diensten laden mislukt')));
  }, [open]);

  const unlinkedItems = setlist.items.filter((item) => !item.song_id || item.link_broken);
  const isNew = target === 'new';
  const service = targets.find((candidate) => candidate.id === target);
  const needsMode = !!service && service.item_count > 0;
  const newValid = newService.title.trim() && newService.service_date && newService.end_time > newService.start_time;
  const ready = confirmed && !busy && (isNew ? newValid : !!service && (!needsMode || mode === 'append'));

  const submit = async () => {
    setBusy(true); setError('');
    try {
      let serviceId = target;
      let expectedVersion = service?.setlist_version;
      if (isNew) {
        if (!createdServiceId.current) {
          createdServiceId.current = (await createService({ ...newService, title: newService.title.trim() })).id;
        }
        serviceId = createdServiceId.current;
        expectedVersion = 0;
      }
      const result = await reuseSetlist(setlist.id, {
        service_id: serviceId, confirm: true, unlinked, mode: needsMode ? 'append' : 'fill_empty', expected_setlist_version: expectedVersion,
      });
      toast.success(`${pluralize(result.copied, 'onderdeel', 'onderdelen')} overgenomen in de planning`);
      onDone(result);
    } catch (err) {
      setError(errorMessage(err, 'Setlist overnemen mislukt'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] w-[calc(100vw-1rem)] max-w-xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Gebruik als nieuwe setlist</DialogTitle>
          <DialogDescription>De liedvolgorde wordt gekopieerd. De historische setlist zelf blijft ongewijzigd.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <ol aria-label="Liederen die worden overgenomen" className="list-decimal space-y-1 pl-5">
            {setlist.items.map((item) => (
              <li key={item.id}>{item.song_title || item.original_title}{(!item.song_id || item.link_broken) && <span className="ml-2 rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800">niet gekoppeld</span>}</li>
            ))}
          </ol>

          {unlinkedItems.length > 0 && (
            <fieldset className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <legend className="flex items-center gap-1 px-1 font-medium text-amber-900"><AlertTriangle className="h-4 w-4" />{pluralize(unlinkedItems.length, 'lied is', 'liederen zijn')} niet gekoppeld</legend>
              <label className="flex min-h-11 items-center gap-2"><input type="radio" name="unlinked" checked={unlinked === 'as_title'} onChange={() => setUnlinked('as_title')} />Overnemen met de oorspronkelijke titel (zonder bibliotheeklied)</label>
              <label className="flex min-h-11 items-center gap-2"><input type="radio" name="unlinked" checked={unlinked === 'skip'} onChange={() => setUnlinked('skip')} />Niet-gekoppelde liederen overslaan</label>
              <p className="text-xs text-amber-900">Er worden nooit nieuwe liederen in de bibliotheek aangemaakt.</p>
            </fieldset>
          )}

          <div className="space-y-2">
            <label htmlFor="reuse-target" className="font-medium text-slate-900">Dienst</label>
            <select id="reuse-target" className="h-11 w-full rounded-md border border-slate-200 bg-white px-3" value={target} onChange={(event) => { setTarget(event.target.value); setMode(''); }}>
              <option value="">Kies een geplande dienst…</option>
              {targets.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>{formatServiceDate(candidate.service_date)} · {candidate.title}{candidate.item_count ? ` (heeft al ${pluralize(candidate.item_count, 'onderdeel', 'onderdelen')})` : ''}</option>
              ))}
              <option value="new">Nieuwe dienst aanmaken…</option>
            </select>
            {loadError && <p className="text-red-600">{loadError}</p>}
          </div>

          {isNew && (
            <div className="grid gap-2 sm:grid-cols-2">
              <Input className="sm:col-span-2" aria-label="Titel nieuwe dienst" placeholder="Titel van de dienst" value={newService.title} onChange={(event) => setNewService({ ...newService, title: event.target.value })} />
              <Input type="date" aria-label="Datum nieuwe dienst" value={newService.service_date} onChange={(event) => setNewService({ ...newService, service_date: event.target.value })} />
              <div className="flex gap-2">
                <Input type="time" aria-label="Starttijd" value={newService.start_time} onChange={(event) => setNewService({ ...newService, start_time: event.target.value })} />
                <Input type="time" aria-label="Eindtijd" value={newService.end_time} onChange={(event) => setNewService({ ...newService, end_time: event.target.value })} />
              </div>
            </div>
          )}

          {needsMode && (
            <div role="alert" className="rounded-md border border-amber-200 bg-amber-50 p-3">
              <p className="font-medium text-amber-900">Deze dienst heeft al een setlist. Die wordt niet overschreven.</p>
              <label className="flex min-h-11 items-center gap-2"><input type="radio" name="mode" checked={mode === 'append'} onChange={() => setMode('append')} />Liederen achteraan toevoegen aan de bestaande setlist</label>
              <p className="text-xs text-amber-900">Kies anders hierboven een lege dienst.</p>
            </div>
          )}

          <label className="flex min-h-11 items-start gap-3"><Checkbox checked={confirmed} onCheckedChange={(value) => setConfirmed(value === true)} aria-label="Bevestig overnemen" className="mt-0.5" /><span>Ik bevestig dat deze liedvolgorde in de gekozen dienst wordt gezet.</span></label>
          {error && <p role="alert" className="text-red-600">{error}</p>}
        </div>
        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" className="min-h-11" onClick={() => onOpenChange(false)}>Annuleren</Button>
          <Button type="button" className="min-h-11 bg-teal-600 text-white hover:bg-teal-700" disabled={!ready} onClick={submit}>{busy ? 'Bezig…' : 'Setlist overnemen'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReuseDialog;
